// Modules
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { uniqBy, debounce } from "lodash";

// Redux
import { selectedFilterData, updatedSelectedFilter } from "redux/userList/userSlice";
import { retroClaimListSelector, getRetroClaimList, getLoadingStateForClaimList } from "redux/retroClaim/retroClaimSlice";
import { filterInputsSelector } from "redux/userList/userSlice";

// Hooks
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";

// Constant
import { Filters as FilterConstant, RetroClaimFilterStatusSort } from "constants/filters";
import { RETRO_CLAIM as Constants, Routes, REVIEW_RETRO_CLAIM_FORM, DateTimeFormats } from "constants/global";

// Component
import ViewEdit from "components/atoms/ViewEdit/ViewEdit";
import Status from "components/atoms/Status/Status";

// Type
import { ClaimDataType } from "redux/types";
import { GroupDetailsType } from "components/atoms/ExpandedRowTable/ExpandedRowTable";

export type ColumnHeaderRowType = {
	title: string;
	dataIndex: string;
	align?: "left" | "right" | "center";
};

export type TableDataType = {
	[key: string]: string;
};

export type FilterSortType = {
	[key: string]: string;
};

export type ClaimListPayloadType = {
	from_receipt_date: string;
	to_receipt_date: string;
	page_number?: number;
	page_records?: number;
	from_net_amount?: number;
	to_net_amount?: number;
	perm_id?: number;
	sort_by?: string;
	sort_order?: string;
	claim_status?: Array<string>;
	receipt_no?: string;
	outlet_code?: string;
};
export type ExpandedRowTableProps = {
	handlePagination?: (pageNumber: number, groupCode?: string) => void;
	columnHeaderData?: Array<ColumnHeaderRowType>; // Type for column headers
	tableData?: Array<TableDataType>; // Use || to add additional Table Data types
	rowsPerPage?: number;
	groupDetails?: GroupDetailsType;
	searchUser?: string;
	receiptFilter?: string;
	outletCodeFilter?: string;
};

type ClaimGroupType = {
	[key: string]: Array<ClaimDataType>;
};

type UserDataType = {
	key: number;
	receipt_datetime: string;
	verified_on: string;
	agentId: string;
	outlet: string;
	source: string;
	tier: string;
	purchaseAmount: number;
	status: JSX.Element;
	netAmount: number;
	review: JSX.Element;
	receiptDatetime: string;
	claimStatus: string;
	claimStatusRaw: string;
};

export type UserListPayloadType = { page_number: number; page_records: number; user_search_text?: string; group_code?: string };

function getVerifiedDate(item: ClaimDataType) {
	if (item?.claim_status === Constants.REJECTED_STATUS) {
		return moment(item?.rejected_on)?.format(DateTimeFormats.USE_DD_MMM);
	}

	if (item?.claim_status === Constants.APPROVED_STATUS) {
		return moment(item?.approved_on)?.format(DateTimeFormats.USE_DD_MMM);
	}

	if (item?.verified_on) {
		return moment(item?.verified_on)?.format(DateTimeFormats.USE_DD_MMM);
	}

	return "";
}

const cleanClaimStatusArray = (statusArray: Array<string | null | undefined>): Array<string> => {
	return statusArray.filter((status): status is string => status !== null && status !== undefined && status !== "");
};

export const useExpandedRowTableForRetroClaim = ({ groupDetails }: ExpandedRowTableProps) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const claimGroup: ClaimGroupType = {};
	const pageSize = Constants.DEFAULT_ROW_PER_PAGE;

	const claimList = useAppSelector(retroClaimListSelector);
	const selectedFilter = useAppSelector(selectedFilterData);
	const loading = useAppSelector(getLoadingStateForClaimList) as unknown as number;
	const filterInputs = useAppSelector(filterInputsSelector);

	const isGroupPendingClaims = groupDetails?.perm_code === Constants.PENDING_GROUP_NAME;
	const keyValue = parseInt(`${groupDetails?.perm_id}${groupDetails?.perm_val2}`);

	const [currentPage, setCurrentPage] = useState(1);

	// Add new state for text filters
	const [receiptFilter, setReceiptFilter] = useState("");
	const [outletCodeFilter, setOutletCodeFilter] = useState("");

	// Create debounced function for API calls
	const debouncedGetClaimList = useMemo(
		() =>
			debounce((payload: ClaimListPayloadType) => {
				dispatch(getRetroClaimList(payload));
			}, 800),
		[dispatch]
	);

	// Add handler for text input changes
	const handleTextFilterChange = (value: string, filterType: string) => {
		// Update local state immediately for UI
		if (filterType === "receipt") {
			setReceiptFilter(value);
		} else if (filterType === "outlet") {
			setOutletCodeFilter(value);
		}

		const payLoad: ClaimListPayloadType = {
			from_receipt_date: moment(moment().subtract(90, "days").calendar()).format(DateTimeFormats.USE_YYYY_MM_DD),
			to_receipt_date: moment().format(DateTimeFormats.USE_YYYY_MM_DD),
			page_number: currentPage,
			page_records: Constants.DEFAULT_ROW_PER_PAGE,
			from_net_amount: isGroupPendingClaims ? 0 : groupDetails?.perm_val1,
			to_net_amount: groupDetails?.perm_val2,
			perm_id: groupDetails?.perm_id,
			receipt_no: filterType === "receipt" ? value : receiptFilter,
			outlet_code: filterType === "outlet" ? value : outletCodeFilter
		};

		debouncedGetClaimList(payLoad);
	};

	const totalRecordCount = useMemo(() => {
		if (claimList && groupDetails) {
			return claimList[keyValue]?.record_count;
		}
	}, [claimList]);

	// On pagination getRetroClaimList will be called
	const handlePagination = (pageNumber: number, group?: GroupDetailsType) => {
		const selectedSortVal = selectedFilter[`${groupDetails?.perm_code}_sort`];

		// Set current page number
		setCurrentPage(pageNumber);

		const payLoad: ClaimListPayloadType = {
			from_receipt_date: moment(moment().subtract(90, "days").calendar()).format(DateTimeFormats.USE_YYYY_MM_DD),
			to_receipt_date: moment().format(DateTimeFormats.USE_YYYY_MM_DD),
			page_number: pageNumber,
			page_records: Constants.DEFAULT_ROW_PER_PAGE,
			from_net_amount: isGroupPendingClaims ? 0 : group?.perm_val1,
			to_net_amount: group?.perm_val2,
			perm_id: group?.perm_id,
			sort_by: "date",
			sort_order: "desc",
			receipt_no: receiptFilter,
			outlet_code: outletCodeFilter
		};

		// Handle status filtering - updated to handle multiple selections
		if (selectedFilter) {
			// Get the array of selected statuses and clean it
			const statusArray = selectedFilter[groupDetails?.perm_code] ? selectedFilter[groupDetails?.perm_code].split(",").filter(Boolean) : [];

			if (statusArray?.length) {
				const mappedStatuses = statusArray.map((status: string) => RetroClaimFilterStatusSort[status]);
				payLoad.claim_status = cleanClaimStatusArray(mappedStatuses);

				// Check if receipt filter is selected
				if (statusArray.includes(FilterConstant.RETRO_CLAIM[0].values[4].value) && filterInputs?.receipt) {
					payLoad.receipt_no = filterInputs.receipt;
				}

				// Check if outlet filter is selected
				if (statusArray.includes(FilterConstant.RETRO_CLAIM[0].values[5].value) && filterInputs?.outlet) {
					payLoad.outlet_code = filterInputs.outlet;
				}
			}
		}

		// Handle sorting
		if (selectedSortVal) {
			if (selectedSortVal === FilterConstant.RETRO_CLAIM[1].values[0].value) {
				payLoad.sort_by = "date";
				payLoad.sort_order = "desc";
			} else if (selectedSortVal === FilterConstant.RETRO_CLAIM[1].values[1].value) {
				payLoad.sort_by = "date";
				payLoad.sort_order = "asc";
			} else if (selectedSortVal === FilterConstant.RETRO_CLAIM[1].values[2].value) {
				payLoad.sort_by = "outlet";
				payLoad.sort_order = "desc";
			} else if (selectedSortVal === FilterConstant.RETRO_CLAIM[1].values[3].value) {
				payLoad.sort_by = "outlet";
				payLoad.sort_order = "asc";
			}
		}

		dispatch(getRetroClaimList(payLoad));
	};

	useEffect(() => {
		// Default payload upon opening any claims table in retro claim. This is aligned with the filter selection
		const payLoad: ClaimListPayloadType = {
			from_receipt_date: moment(moment().subtract(90, "days").calendar()).format(DateTimeFormats.USE_YYYY_MM_DD),
			to_receipt_date: moment().format(DateTimeFormats.USE_YYYY_MM_DD),
			page_number: currentPage,
			page_records: Constants.DEFAULT_ROW_PER_PAGE,
			from_net_amount: isGroupPendingClaims ? 0 : groupDetails?.perm_val1,
			to_net_amount: groupDetails?.perm_val2,
			perm_id: groupDetails?.perm_id,
			sort_by: "date",
			sort_order: "desc",
			receipt_no: receiptFilter,
			outlet_code: outletCodeFilter
		};

		if (isGroupPendingClaims) {
			payLoad["claim_status"] = Constants.PENDING_STATUS_ARRAY;
		}

		dispatch(getRetroClaimList(payLoad));
	}, []);

	// Modify getSortedList to only handle client-side sorting
	const getSortedList = (userData: Array<UserDataType>, selectedFilterVal: string) => {
		// Handle date sorting
		if (selectedFilterVal === FilterConstant.RETRO_CLAIM[1].values[0].value) {
			// Newest to Oldest
			return userData.sort((obj1, obj2) => new Date(obj2.receiptDatetime).setHours(0, 0, 0, 0) - new Date(obj1.receiptDatetime).setHours(0, 0, 0, 0));
		}

		if (selectedFilterVal === FilterConstant.RETRO_CLAIM[1].values[1].value) {
			// Oldest to Newest
			return userData.sort((obj1, obj2) => new Date(obj1.receiptDatetime).setHours(0, 0, 0, 0) - new Date(obj2.receiptDatetime).setHours(0, 0, 0, 0));
		}

		// Handle status filtering
		return uniqBy(
			[
				...userData.filter((item) => {
					if (!selectedFilterVal) {
						return item.claimStatusRaw.toUpperCase() === REVIEW_RETRO_CLAIM_FORM.CLAIM_STATUS_PENDING;
					} else {
						return item.claimStatusRaw.toUpperCase() === RetroClaimFilterStatusSort[selectedFilterVal];
					}
				}),
				...userData
			],
			"key"
		);
	};

	const handleViewEdit = (item: ClaimDataType) => {
		navigate(Routes.REVIEW_RETRO_CLAIM, { state: { claimInfo: item } });
	};
	// Updating the userlist based on the group
	const updatedList = useMemo(() => {
		if (claimList && groupDetails) {
			const userData: Array<UserDataType> = [];
			claimGroup[keyValue] = claimList[keyValue] && claimList[keyValue].claim_list;
			claimGroup[keyValue]?.map((item: ClaimDataType) => {
				const claimDetails = {
					key: item.retro_claim_id,
					receipt_datetime: moment(item?.receipt_datetime)?.format(DateTimeFormats.USE_DD_MMM),
					verified_on: getVerifiedDate(item),
					agentId: item.verified_by || item.created_by,
					outlet: item.outlet_name.trim(),
					source: item.source,
					tier: item.tier_name,
					purchaseAmount: item.purchase_amount,
					status: <Status status={item.claim_status.trim().toLowerCase()} />,
					netAmount: item.net_amount,
					review: <ViewEdit onClick={() => handleViewEdit(item)} />,
					receiptDatetime: item.receipt_datetime,
					claimStatus: item.claim_status.trim().toUpperCase()?.replace("_", " "),
					claimStatusRaw: item.claim_status
				};
				userData.push(claimDetails);
			});
			const selectedFilterVal: string = selectedFilter[groupDetails.perm_code] ?? "";

			return getSortedList(userData, selectedFilterVal);
		}
	}, [claimList, selectedFilter]);

	useEffect(() => {
		if (!groupDetails) return;
		const selectedSortVal = selectedFilter[`${groupDetails.perm_code}_sort`];

		const payLoad: ClaimListPayloadType = {
			from_receipt_date: moment(moment().subtract(90, "days").calendar()).format(DateTimeFormats.USE_YYYY_MM_DD),
			to_receipt_date: moment().format(DateTimeFormats.USE_YYYY_MM_DD),
			page_number: currentPage,
			page_records: Constants.DEFAULT_ROW_PER_PAGE,
			from_net_amount: isGroupPendingClaims ? 0 : groupDetails?.perm_val1,
			to_net_amount: groupDetails?.perm_val2,
			perm_id: groupDetails?.perm_id,
			claim_status: Constants.PENDING_STATUS_ARRAY
		};

		// Handle status filtering - updated to handle multiple selections
		if (selectedFilter) {
			// Get the array of selected statuses and clean it
			const statusArray = selectedFilter[groupDetails.perm_code] ? selectedFilter[groupDetails.perm_code].split(",").filter(Boolean) : [];

			if (statusArray?.length) {
				const mappedStatuses = statusArray.map((status: string) => RetroClaimFilterStatusSort[status]);
				payLoad.claim_status = cleanClaimStatusArray(mappedStatuses);

				// Check if receipt filter is selected
				if (statusArray.includes(FilterConstant.RETRO_CLAIM[0].values[4].value) && filterInputs?.receipt) {
					payLoad.receipt_no = filterInputs.receipt;
				}

				// Check if outlet filter is selected
				if (statusArray.includes(FilterConstant.RETRO_CLAIM[0].values[5].value) && filterInputs?.outlet) {
					payLoad.outlet_code = filterInputs.outlet;
				}
			}
		}
		// Handle sorting
		if (selectedSortVal) {
			if (selectedSortVal === FilterConstant.RETRO_CLAIM[1].values[0].value) {
				payLoad.sort_by = "date";
				payLoad.sort_order = "desc";
			} else if (selectedSortVal === FilterConstant.RETRO_CLAIM[1].values[1].value) {
				payLoad.sort_by = "date";
				payLoad.sort_order = "asc";
			} else if (selectedSortVal === FilterConstant.RETRO_CLAIM[1].values[2].value) {
				payLoad.sort_by = "outlet";
				payLoad.sort_order = "desc";
			} else if (selectedSortVal === FilterConstant.RETRO_CLAIM[1].values[3].value) {
				payLoad.sort_by = "outlet";
				payLoad.sort_order = "asc";
			}
		}

		dispatch(getRetroClaimList(payLoad));
	}, [selectedFilter, groupDetails?.perm_code, filterInputs]);

	useEffect(() => {
		if (isGroupPendingClaims && groupDetails?.perm_code) {
			const defaultFilters = [FilterConstant.RETRO_CLAIM[0].values[0].value, FilterConstant.RETRO_CLAIM[0].values[1].value].join(",");

			dispatch(
				updatedSelectedFilter({
					[groupDetails.perm_code]: defaultFilters
				})
			);
		}
	}, [isGroupPendingClaims, groupDetails]);

	return {
		handlePagination,
		updatedList,
		claimList,
		loading,
		pageSize,
		totalRecordCount,

		handleTextFilterChange,
		receiptFilter,
		outletCodeFilter
	};
};
